import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Palette, Monitor, Smartphone, Save } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ThemePreferences {
  density: 'compact' | 'comfortable' | 'spacious';
  fontSize: number;
  accentColor: string;
  borderRadius: number;
}

interface ThemeCustomizerProps {
  preferences: ThemePreferences;
  onChange: (preferences: ThemePreferences) => void;
  onSave: () => void;
  isSpanish?: boolean;
}

const accentColors = [
  { name: 'Blue', value: '220 90% 56%', preview: 'hsl(220, 90%, 56%)' },
  { name: 'Purple', value: '270 70% 60%', preview: 'hsl(270, 70%, 60%)' },
  { name: 'Green', value: '142 76% 36%', preview: 'hsl(142, 76%, 36%)' },
  { name: 'Orange', value: '25 95% 53%', preview: 'hsl(25, 95%, 53%)' },
  { name: 'Red', value: '0 84% 60%', preview: 'hsl(0, 84%, 60%)' },
  { name: 'Teal', value: '180 62% 45%', preview: 'hsl(180, 62%, 45%)' },
];

export const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({
  preferences,
  onChange,
  onSave,
  isSpanish = false,
}) => {
  const [localPrefs, setLocalPrefs] = useState(preferences);

  useEffect(() => {
    setLocalPrefs(preferences);
  }, [preferences]);

  const handleChange = (key: keyof ThemePreferences, value: any) => {
    const newPrefs = { ...localPrefs, [key]: value };
    setLocalPrefs(newPrefs);
    onChange(newPrefs);
  };

  const applyPreferences = () => {
    // Apply density
    document.documentElement.setAttribute('data-density', localPrefs.density);
    
    // Apply font size
    document.documentElement.style.fontSize = `${localPrefs.fontSize}px`;
    
    // Apply accent color
    document.documentElement.style.setProperty('--primary', localPrefs.accentColor);
    
    // Apply border radius
    document.documentElement.style.setProperty('--radius', `${localPrefs.borderRadius}rem`);
  };

  useEffect(() => {
    applyPreferences();
  }, [localPrefs]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">
            {isSpanish ? 'Personalización de Tema' : 'Theme Customization'}
          </h3>
        </div>
        
        <Button onClick={onSave} size="sm">
          <Save className="w-4 h-4 mr-2" />
          {isSpanish ? 'Guardar Preferencias' : 'Save Preferences'}
        </Button>
      </div>

      {/* Density */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isSpanish ? 'Densidad de Interfaz' : 'Interface Density'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={localPrefs.density}
            onValueChange={(value) => handleChange('density', value as ThemePreferences['density'])}
          >
            <div className="flex items-center space-x-2 p-3 border rounded hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="compact" id="compact" />
              <Label htmlFor="compact" className="cursor-pointer flex-1">
                <div className="font-medium">{isSpanish ? 'Compacto' : 'Compact'}</div>
                <div className="text-sm text-muted-foreground">
                  {isSpanish ? 'Máxima información en pantalla' : 'Maximum information on screen'}
                </div>
              </Label>
              <Monitor className="w-4 h-4 text-muted-foreground" />
            </div>

            <div className="flex items-center space-x-2 p-3 border rounded hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="comfortable" id="comfortable" />
              <Label htmlFor="comfortable" className="cursor-pointer flex-1">
                <div className="font-medium">{isSpanish ? 'Cómodo' : 'Comfortable'}</div>
                <div className="text-sm text-muted-foreground">
                  {isSpanish ? 'Equilibrio entre espacio y densidad' : 'Balance between space and density'}
                </div>
              </Label>
              <Smartphone className="w-4 h-4 text-muted-foreground" />
            </div>

            <div className="flex items-center space-x-2 p-3 border rounded hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="spacious" id="spacious" />
              <Label htmlFor="spacious" className="cursor-pointer flex-1">
                <div className="font-medium">{isSpanish ? 'Espacioso' : 'Spacious'}</div>
                <div className="text-sm text-muted-foreground">
                  {isSpanish ? 'Mayor espacio para facilitar lectura' : 'More space for easier reading'}
                </div>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Font Size */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isSpanish ? 'Tamaño de Fuente' : 'Font Size'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {localPrefs.fontSize}px
            </span>
            <span className="text-sm font-medium" style={{ fontSize: `${localPrefs.fontSize}px` }}>
              {isSpanish ? 'Vista Previa' : 'Preview Text'}
            </span>
          </div>
          <Slider
            value={[localPrefs.fontSize]}
            onValueChange={([value]) => handleChange('fontSize', value)}
            min={12}
            max={18}
            step={1}
          />
        </CardContent>
      </Card>

      {/* Accent Color */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isSpanish ? 'Color de Acento' : 'Accent Color'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {accentColors.map((color) => (
              <button
                key={color.value}
                onClick={() => handleChange('accentColor', color.value)}
                className={cn(
                  "relative p-4 rounded-lg border-2 transition-all hover:scale-105",
                  localPrefs.accentColor === color.value
                    ? "border-primary shadow-lg"
                    : "border-border"
                )}
              >
                <div
                  className="w-full h-12 rounded"
                  style={{ backgroundColor: color.preview }}
                />
                <div className="mt-2 text-sm font-medium">{color.name}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Border Radius */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isSpanish ? 'Redondeo de Bordes' : 'Border Radius'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {localPrefs.borderRadius}rem
            </span>
            <div
              className="w-16 h-16 bg-primary"
              style={{ borderRadius: `${localPrefs.borderRadius}rem` }}
            />
          </div>
          <Slider
            value={[localPrefs.borderRadius]}
            onValueChange={([value]) => handleChange('borderRadius', value)}
            min={0}
            max={1.5}
            step={0.125}
          />
        </CardContent>
      </Card>

      <style dangerouslySetInnerHTML={{ __html: `
        [data-density="compact"] {
          --card-padding: 0.75rem;
          --spacing: 0.5rem;
        }
        
        [data-density="comfortable"] {
          --card-padding: 1.5rem;
          --spacing: 1rem;
        }
        
        [data-density="spacious"] {
          --card-padding: 2rem;
          --spacing: 1.5rem;
        }
      ` }} />
    </div>
  );
};
